import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// Stripe publishable key from environment variable
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 
  'pk_live_51SqWG5LaLpkQZ8P7NNqnR8pJSuaEaHI7yS149IDeCgohRsWZUXgfEWCCs771W1KBmnyS5XCzhsqTqQlc8VB6azTP'
);

interface PaymentFormProps {
  requestId: string;
  onSuccess: () => void;
  onCancel?: () => void;
}

const CheckoutForm: React.FC<{ requestId: string; onSuccess: () => void; onCancel?: () => void }> = ({
  requestId,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?requestId=${requestId}`,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed');
        setIsProcessing(false);
      } else {
        // Payment successful
        onSuccess();
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="btn-primary flex-1 flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <Loader className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            'Pay $19.99'
          )}
        </button>
      </div>
    </form>
  );
};

const PaymentForm: React.FC<PaymentFormProps> = ({ requestId, onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    // Create payment intent when component mounts
    const createPaymentIntent = async () => {
      try {
        const response = await fetch(
          'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod/payments/create-intent',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              requestId,
              amount: 1999, // $19.99 in cents
            }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const data = await response.json();
        setClientSecret(data.clientSecret);
      } catch (err) {
        setError('Failed to initialize payment. Please try again.');
        console.error('Payment intent error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    createPaymentIntent();
  }, [requestId]);

  if (isLoading) {
    return (
      <div className="card text-center py-12">
        <Loader className="w-12 h-12 text-luma-primary mx-auto mb-4 animate-spin" />
        <p className="text-luma-text-light">Initializing payment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Payment Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          {onCancel && (
            <button onClick={onCancel} className="btn-secondary">
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return null;
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#FFB6C1',
        colorBackground: '#ffffff',
        colorText: '#4A4A4A',
        colorDanger: '#FFCDD2',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        borderRadius: '12px',
      },
    },
  };

  return (
    <div className="card max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-soft">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-luma-text mb-2">Complete Your Payment</h2>
        <p className="text-luma-text-light mb-4">
          Your consultation is complete. Please pay $19.99 to finalize.
        </p>
        <div className="inline-block bg-luma-accent px-6 py-3 rounded-xl">
          <p className="text-3xl font-bold text-luma-primary">$19.99</p>
          <p className="text-sm text-luma-text-light">One-time consultation fee</p>
        </div>
      </div>

      <Elements stripe={stripePromise} options={options}>
        <CheckoutForm requestId={requestId} onSuccess={onSuccess} onCancel={onCancel} />
      </Elements>

      <div className="mt-6 pt-6 border-t border-luma-border">
        <p className="text-xs text-luma-text-light text-center">
          🔒 Secure payment powered by Stripe. Your payment information is encrypted and secure.
        </p>
      </div>
    </div>
  );
};

export default PaymentForm;
